/**
 * "How are operations running?" — a question that crosses every screen.
 *
 * The section readings answer about the screen in front of you. This answers
 * about a *department*, which is not the same shape at all: operations is work
 * orders and alerts and inspections read together, and the useful sentence is
 * the one that only appears when you put them side by side. Three open alerts
 * is a fact; three open alerts with nothing raised from them while the crews
 * are at capacity is a reading.
 *
 * Two things this file is careful about.
 *
 * **Slip is measured against the current baseline.** A project that has been
 * rebaselined three times can show zero slip and be a year late, and the
 * platform is honest enough to carry the rebaseline count — so this reports it
 * beside the slip rather than quoting the slip alone. Quoting slip from a moved
 * baseline is the most common way a portfolio looks healthy.
 *
 * **Money is as declared.** The platform computes nothing from budget figures
 * and neither does this. Where a project has not declared its money, it is
 * reported as undeclared rather than as zero — a project with no budget on file
 * is not a project within budget.
 */
import type { DataSource, Principal } from "../api/dataSource";
import { PERMISSION, hasPermission } from "../api/dataSource";
import { isOpen } from "../domain/workOrders";
import type { PageAnswer } from "./pages";
import type { Suggestion } from "./types";

export type Department = "operations" | "maintenance" | "projects";

export interface BriefingContext {
  source: DataSource;
  siteId: string;
  siteCode: string;
  principal: Principal;
  now: number;
}

const daysSince = (iso: string | undefined, now: number): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.round((now - t) / 86_400_000) : null;
};

/** Which department a question is asking about, or null. */
export function departmentIn(q: string): Department | null {
  const t = q.toLowerCase();
  if (/\b(project|projects|capital|portfolio|gate|gates|hold point)\b/.test(t)) return "projects";
  if (/\b(maintenance|maintaining|upkeep|servicing|defect|defects)\b/.test(t)) return "maintenance";
  if (/\b(operation|operations|ops|airfield|running|the estate|overall|everything)\b/.test(t)) return "operations";
  return null;
}

/** "how are X running", "any issues in X", "what is late" — a briefing, not a search. */
export function isBriefingRequest(q: string): boolean {
  const t = q.toLowerCase();
  return (
    /\b(how (is|are|'s)|how's|any (issues|problems)|what(?:'s| is| are)? (running late|late|behind|over budget|at risk)|status of|report on|brief me on|give me|pull|prepare)\b/.test(
      t,
    ) || /\b(running late|over budget|behind schedule|at risk)\b/.test(t)
  );
}

/* ── operations ───────────────────────────────────────────────────────── */

async function operations(ctx: BriefingContext): Promise<PageAnswer | null> {
  const { source, siteId, siteCode, principal, now } = ctx;
  if (!hasPermission(principal, PERMISSION.workOrdersRead)) return null;

  const [wos, alerts, insp] = await Promise.all([
    source.listWorkOrders({ siteId }),
    hasPermission(principal, PERMISSION.insightRead) ? source.listAlerts(siteId).catch(() => []) : Promise.resolve([]),
    hasPermission(principal, PERMISSION.inspectionsRead)
      ? source.listInspections({ siteId }).catch(() => [])
      : Promise.resolve([]),
  ]);

  const open = wos.items.filter((w) => isOpen(w.status));
  const p1 = open.filter((w) => w.priority === 1);
  const overdue = open.filter((w) => {
    const d = daysSince(w.dueAt, now);
    return d !== null && d > 0;
  });
  const openAlerts = alerts.filter((a) => a.status === "open");
  const raisedFrom = new Set(wos.items.map((w) => w.alertId).filter(Boolean));
  const unraised = openAlerts.filter((a) => !raisedFrom.has(a.id));
  const inspOverdue = insp
    .filter((i) => i.status === "scheduled" || i.status === "in_progress")
    .filter((i) => {
      const d = daysSince(i.scheduledFor, now);
      return d !== null && d > 0;
    });

  /* The sentence that only appears when the three are read together. */
  const cross: string[] = [];
  if (unraised.length > 0 && overdue.length > 0) {
    cross.push(
      `${unraised.length} alert${unraised.length === 1 ? "" : "s"} with nothing raised from ${unraised.length === 1 ? "it" : "them"}, while ${overdue.length} of the work already raised ${overdue.length === 1 ? "is" : "are"} past its date. Raising more against a queue that is not clearing moves the backlog rather than the airfield.`,
    );
  } else if (unraised.length > 0) {
    cross.push(
      `${unraised.length} open alert${unraised.length === 1 ? " has" : "s have"} no work raised from ${unraised.length === 1 ? "it" : "them"}, and the queue is otherwise current — so raising them is the cheap move.`,
    );
  }
  if (inspOverdue.length > 0) {
    cross.push(
      `${inspOverdue.length} inspection${inspOverdue.length === 1 ? " is" : "s are"} past date. Missed sheets are how a defect first fails to be found, so a clean work queue with missed inspections is not the good news it reads as.`,
    );
  }
  if (p1.length > 0) {
    const oldestP1 = Math.max(0, ...p1.map((w) => daysSince(w.openedAt, now) ?? 0));
    cross.push(
      `${p1.length} at P1, oldest ${oldestP1} days open. P1 is safety critical; age on it is the number that matters.`,
    );
  }

  const suggestions: Suggestion[] = [];
  if (unraised.length) {
    suggestions.push({
      id: "ops-raise",
      label: "Raise work against the alerts that have none",
      because: `${unraised.length} open with nothing on them.`,
      tradeOff: "Puts them on the record. It also lengthens a queue that already has overdue work in it.",
    });
  }
  if (overdue.length) {
    suggestions.push({
      id: "ops-clear",
      label: "Clear or re-date the overdue work first",
      because: "A queue with stale dates stops being believed, and then nothing in it is trusted.",
      tradeOff: "Re-dating is honest but loses the evidence of how long it really took.",
    });
  }

  return {
    brief: [
      `${siteCode} operations: ${open.length} work orders open (${p1.length} P1, ${overdue.length} overdue), ${openAlerts.length} alerts open, ${inspOverdue.length} inspections past date.`,
      ...cross.slice(0, 2),
    ],
    full: [
      `${siteCode} — operations across work, alerts and inspections.`,
      "",
      `Work: ${open.length} open of ${wos.items.length}. P1 ${p1.length}. Overdue ${overdue.length}. Awaiting verification ${wos.items.filter((w) => w.status === "completed").length}.`,
      `Alerts: ${openAlerts.length} open, ${openAlerts.filter((a) => a.severity >= 4).length} at severity 4–5, ${unraised.length} with nothing raised.`,
      `Inspections: ${insp.length} on file, ${inspOverdue.length} past their date.`,
      "",
      "What that says:",
      ...cross,
    ],
    suggestions,
    sourceLabel: `Operations · ${siteCode}`,
    sourceDetail: `${wos.items.length} work orders, ${alerts.length} alerts, ${insp.length} inspections`,
  };
}

/* ── maintenance ──────────────────────────────────────────────────────── */

async function maintenance(ctx: BriefingContext): Promise<PageAnswer | null> {
  const { source, siteId, siteCode, principal, now } = ctx;
  if (!hasPermission(principal, PERMISSION.workOrdersRead)) return null;

  const [wos, docs] = await Promise.all([
    source.listWorkOrders({ siteId }),
    hasPermission(principal, PERMISSION.documentsRead)
      ? source.listDocuments({ siteId }).catch(() => [])
      : Promise.resolve([]),
  ]);
  const open = wos.items.filter((w) => isOpen(w.status));
  const onHold = open.filter((w) => w.status === "on_hold");
  const preventive = open.filter((w) => w.kind === "preventive");
  const corrective = open.filter((w) => w.kind === "corrective");
  const overdue = open.filter((w) => {
    const d = daysSince(w.dueAt, now);
    return d !== null && d > 0;
  });
  const certsDue = docs.filter((d) => {
    if (d.status !== "issued" || !d.expiresOn) return false;
    const days = -(daysSince(d.expiresOn, now) ?? 0);
    return days <= 30;
  });

  const cross: string[] = [];
  /*
   * The ratio that says whether maintenance is being run or merely responded
   * to. It is a reading of posture, not of volume — and it is only a signal,
   * which is why it is worded as one.
   */
  if (open.length > 0) {
    const ratio = corrective.length / Math.max(1, preventive.length);
    cross.push(
      preventive.length === 0
        ? `All ${open.length} open jobs are corrective or better; nothing preventive is open. A queue with no preventive work in it is a queue that is responding, not maintaining.`
        : `${corrective.length} corrective against ${preventive.length} preventive open (${ratio.toFixed(1)}:1). The higher that runs, the more of the estate is being fixed after it fails rather than before.`,
    );
  }
  if (onHold.length > 0) {
    cross.push(
      `${onHold.length} on hold. A hold without a reason recorded is indistinguishable from work that was forgotten, and it is the state work disappears into.`,
    );
  }
  if (certsDue.length > 0) {
    cross.push(
      `${certsDue.length} certificate${certsDue.length === 1 ? "" : "s"} expiring within 30 days — calibration on a measuring device included, which invalidates the readings taken after it lapses rather than merely being late paperwork.`,
    );
  }

  return {
    brief: [
      `${siteCode} maintenance: ${open.length} open — ${corrective.length} corrective, ${preventive.length} preventive, ${onHold.length} on hold, ${overdue.length} overdue.`,
      ...cross.slice(0, 2),
    ],
    full: [
      `${siteCode} — maintenance posture.`,
      "",
      `Open by kind: ${["corrective", "preventive", "predictive", "inspection", "project"].map((k) => `${k} ${open.filter((w) => w.kind === k).length}`).join(", ")}.`,
      `On hold ${onHold.length}. Overdue ${overdue.length}.`,
      ...(certsDue.length
        ? [`Certificates within 30 days: ${certsDue.map((d) => d.reference ?? d.title).join(", ")}.`]
        : []),
      "",
      "What that says:",
      ...cross,
    ],
    suggestions: onHold.length
      ? [
          {
            id: "maint-holds",
            label: `Read the ${onHold.length} on hold and record why`,
            because: "Work disappears into the hold state.",
            tradeOff: "Costs a read of each. Some are legitimately held and will stay held.",
          },
        ]
      : undefined,
    sourceLabel: `Maintenance · ${siteCode}`,
    sourceDetail: `${open.length} open jobs`,
  };
}

/* ── projects ─────────────────────────────────────────────────────────── */

async function projects(ctx: BriefingContext): Promise<PageAnswer | null> {
  const { source, siteId, siteCode, principal } = ctx;
  if (!hasPermission(principal, PERMISSION.projectsRead)) return null;

  const [rows, holds] = await Promise.all([
    source.listProjects({ siteId }),
    source.listHoldPoints(siteId).catch(() => null),
  ]);
  if (rows.length === 0) return null;

  const slipped = rows.filter((r) => r.schedule?.state === "slipped");
  const overdue = rows.filter((r) => (r.schedule?.overdueDays ?? 0) > 0);
  const rebaselined = rows.filter((r) => (r.schedule?.rebaselined ?? 0) > 0);
  const noBaseline = rows.filter((r) => r.schedule?.state === "no_baseline" || r.schedule?.state === "no_dates");

  const declared = rows.filter((r) => r.money?.state === "declared");
  const undeclared = rows.length - declared.length;
  /*
   * Spent and committed are compared to the budget separately, never summed.
   *
   * Whether "committed" already contains "spent" is not defined by the wire
   * format, so adding them may double-count — and a portfolio review told that
   * six of seven projects are over budget, when they are not, is worse than
   * one told nothing. Over-spend is the harder fact; over-commitment is the
   * earlier warning. They are different sentences.
   */
  const overSpent = declared.filter((r) => (r.money.spentMinor ?? 0) > (r.money.budgetMinor ?? Infinity));
  const overCommitted = declared.filter(
    (r) => (r.money.committedMinor ?? 0) > (r.money.budgetMinor ?? Infinity) && !overSpent.includes(r),
  );

  const cross: string[] = [];
  if (slipped.length || overdue.length) {
    cross.push(
      `${slipped.length} slipped against ${slipped.length === 1 ? "its" : "their"} current baseline, ${overdue.length} past ${overdue.length === 1 ? "its" : "their"} end date.`,
    );
  } else {
    cross.push("Nothing is slipped against its current baseline.");
  }
  /*
   * The insight that matters most in a portfolio review, and the one a slip
   * column alone hides.
   */
  if (rebaselined.length > 0) {
    cross.push(
      `${rebaselined.length} ${rebaselined.length === 1 ? "project has" : "projects have"} been rebaselined — slip is measured against the *current* baseline, so a project that has moved its own target can show no slip and still be late. Read those against the original dates, not the column.`,
    );
  }
  if (overSpent.length > 0) {
    cross.push(
      `${overSpent.length} ${overSpent.length === 1 ? "has" : "have"} spent more than the declared budget — that is money gone, not money at risk.`,
    );
  }
  if (overCommitted.length > 0) {
    cross.push(
      `${overCommitted.length} ${overCommitted.length === 1 ? "has" : "have"} committed above budget without having spent it yet, which is the point at which it can still be stopped.`,
    );
  }
  if (undeclared > 0) {
    cross.push(
      `${undeclared} of ${rows.length} ${undeclared === 1 ? "has" : "have"} not declared money at all. That is not within budget — it is unknown, and any portfolio total built from the rest quietly treats it as zero.`,
    );
  }
  if (holds?.state === "work_stopped") {
    cross.push(
      `${holds.count} hold point${holds.count === 1 ? " is" : "s are"} placed and work is stopped — that is a signature owed, not a delay to manage.`,
    );
  }
  if (noBaseline.length > 0) {
    cross.push(
      `${noBaseline.length} ${noBaseline.length === 1 ? "has" : "have"} no baseline to judge against, so “on time” cannot be said of ${noBaseline.length === 1 ? "it" : "them"} either way.`,
    );
  }

  const suggestions: Suggestion[] = [];
  if (rebaselined.length) {
    suggestions.push({
      id: "proj-rebaseline",
      label: "Review the rebaselined projects against their original dates",
      because: "Slip against a moved baseline understates by exactly the amount it was moved.",
      tradeOff: "It is an uncomfortable read and it is the only one that shows the real delay.",
    });
  }
  if (undeclared) {
    suggestions.push({
      id: "proj-money",
      label: `Get money declared on the ${undeclared} that have none`,
      because: "An undeclared budget reads as zero in every total.",
      tradeOff: "Somebody has to commit to a number. That is the point and the reason it has not happened.",
    });
  }
  if (holds?.state === "work_stopped") {
    suggestions.push({
      id: "proj-holds",
      label: "Work the hold points that are stopping work",
      because: `${holds.count} placed.`,
      tradeOff:
        "Each needs the inspection recorded and then, separately, the release signed. Collapsing the two is the failure the hold point exists to prevent.",
    });
  }

  return {
    brief: [
      `${siteCode}: ${rows.length} projects — ${slipped.length} slipped, ${overdue.length} past end date, ${overSpent.length} overspent, ${overCommitted.length} over-committed, ${undeclared} with no money declared.`,
      ...cross.slice(rebaselined.length ? 1 : 0, rebaselined.length ? 2 : 2),
    ],
    full: [
      `${siteCode} — capital projects.`,
      "",
      `By status: ${[...new Set(rows.map((r) => r.status))].map((st) => `${st} ${rows.filter((r) => r.status === st).length}`).join(", ")}.`,
      `Schedule: ${[...new Set(rows.map((r) => r.schedule?.state))]
        .filter(Boolean)
        .map((st) => `${st} ${rows.filter((r) => r.schedule?.state === st).length}`)
        .join(", ")}.`,
      `Money: ${declared.length} declared, ${undeclared} not. Spent over budget ${overSpent.length}; committed over budget ${overCommitted.length}. Compared separately, never summed — the wire format does not say whether committed already contains spent.`,
      ...(holds ? [`Hold points: ${holds.count} placed, state ${holds.state}.`] : []),
      "",
      "What that says:",
      ...cross,
      ...(slipped.length
        ? [
            "",
            "Slipped:",
            ...slipped
              .slice(0, 5)
              .map((r) => `• ${r.code} ${r.name} — ${r.schedule.why ?? `${r.schedule.endSlipDays ?? "?"} days`}`),
          ]
        : []),
    ],
    suggestions,
    sourceLabel: `Projects · ${siteCode}`,
    sourceDetail: `${rows.length} projects`,
  };
}

const BRIEFINGS: Record<Department, (ctx: BriefingContext) => Promise<PageAnswer | null>> = {
  operations,
  maintenance,
  projects,
};

export async function briefingFor(dept: Department, ctx: BriefingContext): Promise<PageAnswer | null> {
  try {
    return await BRIEFINGS[dept](ctx);
  } catch {
    return null;
  }
}
